// Datos de la unidad y garantías legales (ADR-0144 §1, §3 y §5).
//
// Tres cosas puras, sin base ni transporte:
//
//   1. La ficha canónica de la unidad dentro de `assets.attributes`
//      (tipología, estacionamientos, bodegas y las tres fechas). Un
//      esquema para validar escrituras y un lector tolerante para la
//      lectura, igual que `assetGeoSchema` / `parseAssetGeo`: un valor
//      malformado en la base se lee como ausente, nunca revienta la ruta.
//   2. Los contactos de emergencia del proyecto, en
//      `asset_groups.attributes.emergencyContacts[]`.
//   3. El cálculo de garantías por partida desde los pisos del art. 18 de
//      la LGUC: estructura 10 años e instalaciones 5 años desde la
//      recepción definitiva de la DOM; terminaciones 3 años desde la
//      inscripción en el CBR. Sin fecha, la partida queda «por confirmar»:
//      nunca se inventa una fecha ni se cae a la fecha de entrega (que es
//      justamente la alternativa que el ADR descarta).
//
// Vive en el engine y no en api-types porque es regla de dominio (como
// `deriveObservationSeverity` o el puntaje de facility), no forma de wire:
// core-api la usa para proyectar y un importador futuro la usará para
// validar. Las etiquetas visibles de cada partida viven en
// `@bitacorapro/api-types` (unit-profile-labels.ts), como el resto del
// vocabulario que pintan los clientes.
import { z } from "zod";
// ── 1. Ficha de la unidad ─────────────────────────────────────────────
/** Claves canónicas de la ficha dentro de `assets.attributes`. Snake case
 *  como el resto de las claves de postventa (`owner_name`,
 *  `asset_status`). */
export const UNIT_PROFILE_ATTRIBUTE_KEYS = {
    tipologia: "tipologia",
    estacionamientos: "estacionamientos",
    bodegas: "bodegas",
    fechaRecepcionDom: "fecha_recepcion_dom",
    fechaInscripcionCbr: "fecha_inscripcion_cbr",
    fechaEntrega: "fecha_entrega",
};
/** Las claves que sólo el importador y el staff escriben. La receta las
 *  suma a `protectedAttributeKeys` para que el propietario no las mueva
 *  (ADR-0144 §1). */
export const UNIT_PROFILE_PROTECTED_KEYS = Object.values(UNIT_PROFILE_ATTRIBUTE_KEYS);
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** `YYYY-MM-DD` que además es una fecha real del calendario (rechaza
 *  2025-02-29 y 2025-13-01, que el regex solo dejaría pasar). */
export function isIsoCalendarDate(value) {
    const m = ISO_DATE_RE.exec(value);
    if (!m)
        return false;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (mo < 1 || mo > 12 || d < 1)
        return false;
    return d <= daysInMonth(y, mo);
}
export const isoCalendarDateSchema = z
    .string()
    .refine(isIsoCalendarDate, { message: "expected a calendar date YYYY-MM-DD" });
/** Fecha de entrega: fecha y hora ISO, o sólo la fecha cuando la planilla
 *  de la inmobiliaria no trae la hora. */
const fechaEntregaSchema = z.union([isoCalendarDateSchema, z.string().datetime({ offset: true })]);
const etiquetaSchema = z.string().trim().min(1).max(40);
/** Esquema de escritura de la ficha. Todas las claves son opcionales —una
 *  unidad puede no tener bodega, o no tener todavía la inscripción en el
 *  CBR— pero si una clave viene, viene bien formada. `passthrough`
 *  porque convive en el mismo JSONB con las claves de la activación y del
 *  acta. */
export const unitProfileAttributesSchema = z
    .object({
    tipologia: z.string().trim().min(1).max(40).optional(),
    estacionamientos: z.array(etiquetaSchema).max(10).optional(),
    bodegas: z.array(etiquetaSchema).max(10).optional(),
    fecha_recepcion_dom: isoCalendarDateSchema.optional(),
    fecha_inscripcion_cbr: isoCalendarDateSchema.optional(),
    fecha_entrega: fechaEntregaSchema.optional(),
})
    .passthrough();
function readString(raw, schema) {
    const r = schema.safeParse(raw);
    return r.success ? r.data : null;
}
function readLabels(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const v of raw) {
        const r = etiquetaSchema.safeParse(v);
        if (r.success)
            out.push(r.data);
    }
    return out;
}
/** Lector tolerante: cada clave se valida por separado y la malformada se
 *  lee como ausente, sin arrastrar a las demás. */
export function parseUnitProfile(attributes) {
    const a = attributes ?? {};
    const K = UNIT_PROFILE_ATTRIBUTE_KEYS;
    return {
        tipologia: readString(a[K.tipologia], z.string().trim().min(1).max(40)),
        estacionamientos: readLabels(a[K.estacionamientos]),
        bodegas: readLabels(a[K.bodegas]),
        fechaRecepcionDom: readString(a[K.fechaRecepcionDom], isoCalendarDateSchema),
        fechaInscripcionCbr: readString(a[K.fechaInscripcionCbr], isoCalendarDateSchema),
        fechaEntrega: readString(a[K.fechaEntrega], fechaEntregaSchema),
    };
}
// ── 2. Contactos de emergencia del proyecto ──────────────────────────
export const emergencyContactSchema = z.object({
    nombre: z.string().trim().min(1).max(120),
    horario: z.string().trim().min(1).max(120).optional(),
    // Lo que se marca: dígitos, espacios, `+`, guiones y paréntesis. Un
    // número corto como «132» (Bomberos) es válido.
    telefono: z
        .string()
        .trim()
        .min(3)
        .max(30)
        .regex(/^\+?[0-9 ()-]+$/, { message: "expected a dialable phone number" }),
});
export const emergencyContactsSchema = z.array(emergencyContactSchema).max(10);
/** Clave dentro de `asset_groups.attributes`. Camel case porque así la
 *  fija ADR-0144 §5. */
export const EMERGENCY_CONTACTS_ATTRIBUTE_KEY = "emergencyContacts";
/** Lector tolerante de los contactos del proyecto: descarta las entradas
 *  malformadas en vez de esconder la lista completa. */
export function parseEmergencyContacts(groupAttributes) {
    const raw = groupAttributes?.[EMERGENCY_CONTACTS_ATTRIBUTE_KEY];
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const c of raw.slice(0, 10)) {
        const r = emergencyContactSchema.safeParse(c);
        if (r.success)
            out.push(r.data);
    }
    return out;
}
// ── 3. Garantías legales ─────────────────────────────────────────────
export const WARRANTY_PARTIDAS = ["estructura", "instalaciones", "terminaciones"];
export const WARRANTY_STARTS = ["dom", "cbr"];
export const WARRANTY_ESTADOS = ["vigente", "vencida", "por_confirmar"];
/** Los pisos del art. 18 de la LGUC (Ley 20.016). La inmobiliaria puede
 *  alargarlos, nunca acortarlos. «Defectos que no encajan en las
 *  anteriores» (5 años desde DOM) no es una partida que el propietario
 *  elija: comparte plazo y fecha con instalaciones. */
export const LEGAL_WARRANTY_FLOORS = {
    estructura: { years: 10, desde: "dom" },
    instalaciones: { years: 5, desde: "dom" },
    terminaciones: { years: 3, desde: "cbr" },
};
/** Garantía comercial más larga, en años, por partida. Se configura por
 *  proyecto (`asset_groups.attributes.warrantyYears`). */
export const warrantyExtensionsSchema = z
    .object({
    estructura: z.number().int().positive().max(50).optional(),
    instalaciones: z.number().int().positive().max(50).optional(),
    terminaciones: z.number().int().positive().max(50).optional(),
})
    .strict();
export const WARRANTY_EXTENSIONS_ATTRIBUTE_KEY = "warrantyYears";
/** Lector tolerante de la extensión comercial del proyecto. */
export function parseWarrantyExtensions(groupAttributes) {
    const r = warrantyExtensionsSchema.safeParse(groupAttributes?.[WARRANTY_EXTENSIONS_ATTRIBUTE_KEY]);
    return r.success ? r.data : {};
}
function isLeapYear(y) {
    return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}
function daysInMonth(y, m) {
    if (m === 2)
        return isLeapYear(y) ? 29 : 28;
    return [4, 6, 9, 11].includes(m) ? 30 : 31;
}
const pad = (n, w = 2) => String(n).padStart(w, "0");
/**
 * Suma años a una fecha de calendario. El plazo termina el día del mismo
 * número (CC art. 48: «el primero y último día de un plazo de meses o
 * años deberán tener un mismo número en los respectivos meses»). Para un
 * 29 de febrero que cae en un año no bisiesto, ese número no existe y se
 * usa el último día del mes, el 28.
 */
export function addYearsToIsoDate(iso, years) {
    const m = ISO_DATE_RE.exec(iso);
    if (!m || !isIsoCalendarDate(iso))
        throw new Error(`invalid ISO date: ${iso}`);
    const y = Number(m[1]) + years;
    const mo = Number(m[2]);
    const d = Math.min(Number(m[3]), daysInMonth(y, mo));
    return `${pad(y, 4)}-${pad(mo)}-${pad(d)}`;
}
/**
 * Garantías por partida desde las dos fechas de inicio. Siempre devuelve
 * las tres partidas, en el orden de `WARRANTY_PARTIDAS`.
 *
 * - Sin la fecha que la partida necesita (o con una fecha inválida), la
 *   partida queda `por_confirmar` con `vence: null`.
 * - `vigente` mientras `today <= vence`; `vencida` desde el día siguiente.
 */
export function computeUnitWarranties(dates, options) {
    if (!isIsoCalendarDate(options.today)) {
        throw new Error(`invalid today: ${options.today}`);
    }
    const start = {
        dom: dates.fechaRecepcionDom && isIsoCalendarDate(dates.fechaRecepcionDom)
            ? dates.fechaRecepcionDom
            : null,
        cbr: dates.fechaInscripcionCbr && isIsoCalendarDate(dates.fechaInscripcionCbr)
            ? dates.fechaInscripcionCbr
            : null,
    };
    return WARRANTY_PARTIDAS.map((partida) => {
        const floor = LEGAL_WARRANTY_FLOORS[partida];
        const ext = options.extensions?.[partida];
        const plazoAnios = ext !== undefined && Number.isInteger(ext) && ext > floor.years ? ext : floor.years;
        const desde = start[floor.desde];
        if (desde === null) {
            return { partida, plazoAnios, desde: floor.desde, vence: null, estado: "por_confirmar" };
        }
        const vence = addYearsToIsoDate(desde, plazoAnios);
        // Comparación lexicográfica: válida para `YYYY-MM-DD` de 4 dígitos.
        const estado = options.today <= vence ? "vigente" : "vencida";
        return { partida, plazoAnios, desde: floor.desde, vence, estado };
    });
}
//# sourceMappingURL=unit-profile.js.map